/**
 * 应用主入口 - Vite ESM 模式
 *
 * 原飞书版本用 CDN + Babel standalone,所有文件以 `window.X` 全局暴露;
 * 本地化后将各文件改为 ESM 模块 (named export + default export),最后
 * 在 main.jsx 集中挂载到 window 上,保证 harness/components 里的
 * `window.ContentService` / `window.POSTS` 等老引用不破。
 *
 * 注意:React 由 vite.config.js 的 esbuild.jsxInject 全局注入,
 *      这里不要再显式 import 以免重复。
 */
import ReactDOM from 'react-dom/client';

// 样式表入口。线上产物把 CSS 打成独立的 /assets/index-<hash>.css 并由
// <link> 引入,只有当某个 JS 模块 import 了 CSS 时 Vite 才会这么做。
// 还原出的源码里没有这条 import(源码不含样式表本身),必须补回。
import './app.css';
// T-Cron-2026-10-22 08:00 早间:无障碍补丁(全局 focus-visible 焦点环 +
// prefers-reduced-motion 降级)。必须排在 app.css 之后,这样同特异性的
// 规则(如 .post-card:focus-visible)能覆盖 app.css 里的旧声明。
import './a11y.css';
// T-Cron-2026-10-23 20:00 晚间:暗色模式可读性补全 + 分块复制按钮样式。
// 必须排在 a11y.css 之后(app.css → a11y.css → polish.css),
// 这样同特异性的 token 覆盖能生效。
import './polish.css';
// T-Cron-2026-10-24 02:00 早间:改进清单 #8「批量操作」+ #13「性能」。
// 必须排在最后(app.css → a11y.css → polish.css → batch.css),
// 这样同特异性的规则(如 .post-card.selected 的边框)才能覆盖前面的旧声明。
import './batch.css';
// T-Cron-2026-10-25 02:00 早间:改进清单 #7「拖拽排序」的样式(手柄 /
// 落点指示线 / 排序提示)。必须排在 batch.css 之后(最后一个),
// 这样 .chip.is-dragging / .chip.is-drag-over 的同特异性规则
// 能覆盖 app.css 里 .chip 的旧声明。
import './drag.css';
// T-Cron-2026-10-25 20:00 晚间:改进清单 #14「数据:CSV/JSON 导出」的 split button
// 菜单样式(主按钮 CSV + ▾ 露出 JSON)。必须排在最后,这样 .header-export-menu
// 里的动画/尺寸声明能覆盖 app.css 里同名的旧规则。
// ⚠️ 这份 CSS 是 14:00 轮写好但**没部署**的孤儿文件 —— 本轮补上 import。
import './export-menu.css';
// T-Cron-2026-10-25 20:00 晚间:改进清单 #10「无障碍」的标题层级 reset。
// ContentGrid 把日期分组升级成真 <h2> 后,浏览器默认的 h2 margin/font-size
// 会静默改变日期书签的间距和字号 ⇒ 必须显式钉死成原值。
// 必须排在最后,这样 h2.date-bookmark 能覆盖 app.css / polish.css。
import './a11y-headings.css';
// T-Cron-2026-10-28 20:00 晚间:改进清单 #13「性能:虚拟滚动 + 触底加载更多」
// 的样式(触底加载按钮 / 加载更早 / 已加载进度提示)。
// 必须排在最后(在 batch.css / drag.css / export-menu.css 之后),
// 这样 .feed-load-more-btn 的同特异性规则能覆盖前面的旧声明。
import './feed-virtual.css';
// T-Cron-2026-10-31 03:30 早间:改进清单「触感反馈」。
// touch-action / overscroll-behavior —— 整棵树此前 0 命中,移动端一直在默默
// 付 300ms 点击延迟 + 随时被下拉刷新抢走页面。必须排在最后(所有样式表之后),
// 这样 .chip:active / .date-item:active 的 active 反馈能覆盖 app.css 里
// 同名但缺失的声明;body 的 touch-action 是行为层开关,不受顺序影响。
import './touch.css';
// T-Cron-2026-11-01 08:00 早间:改进清单 #15「标签云」的样式。
// 标签云是内聚的视觉子系统(容器 + 3 档字号 + 3 档字重 + 移动端 + 触控目标),
// 独立成文件,避免继续往历史累积的 app.css/polish.css 里插段落。
// 排在最后 ⇒ 同特异性下能覆盖 .chip / .mobile-filter-chip 的基础字号。
import './tag-cloud.css';
// T-Cron-2026-11-02 03:30 早间:改进清单 #12「暗色模式」+ #10「无障碍」——
// WCAG AA 对比度批量修复(--text-accent / --on-accent / 暗色 --text-tertiary)。
// 独立成文件,避免继续往历史累积的 app.css/polish.css 里插段落。
// 必须排在最后(在 tag-cloud.css 之后)⇒ 同特异性下能覆盖 app.css / polish.css
// 的旧声明(--text-accent 的旧值是 #FF2442,与 --accent 相同,等于没区分)。
import './a11y-contrast.css';
// T-Cron-2026-11-04 14:00 (T145) — 改进清单 #10「无障碍」:移动端触控区尺寸。
// 把「移动端可点区域 ≥44px」收敛成一个自洽的子系统(基线 + 每个控件显式声明),
// 不再依赖「某处声明、这里靠继承」—— 那种写法在 tag-cloud.css 上已经失效过
// (app.css 的 `.mobile-filter-chip{min-height:44px}` 特异性 (0,1,0)
//  被 tag-cloud.css 的 `.tag-cloud .tag-cloud-chip{min-height:28px}` (0,2,0) 覆盖
//  ⇒ 44px 从未生效,25 颗标签云 chip 实际只有 31~36px)。
// 必须排在**所有**样式表之后 ⇒ 同特异性下覆盖前面的旧尺寸声明。
import './touch-targets.css';

// Data layer
import { DATA_META, POSTS, BOOK_COVERS, getBookCover } from './data/index.js';

// Harness
import ContentService from './harness/contentService.js';
import CoverDownload from './harness/coverDownload.js';
import useContentStore from './harness/useContentStore.jsx';
// T-Cron-2026-10-22 08:00 早间:焦点陷阱 hook(挂在 window 上,组件直接用
// window.useFocusTrap —— 和 CoverDownload / ContentService 同一套约定)。
import './harness/useFocusTrap.js';

// View components
import App from './App.jsx';
// T-Cron-2026-10-28 20:00 晚间 (T121) — 错误边界。
//
// 🔴 之前**全树没有错误边界** ⇒ 任何渲染期异常都会让 React 卸载整棵树,
//   看板变成白屏,而且用户既不知道为什么,也没法恢复(只能手动刷新)。
//   对每天用 4 次、承载 500+ 条选题的看板来说,这是最高的故障等级。
//
// 挂在 root 外面(而不是包住 App 内部某一段)是有意的:
//   边界本身如果也渲染失败,就没有第二道兜底了 —— 顶层是最后一道。
import ErrorBoundary from './components/ErrorBoundary.jsx';

// 兼容老代码:把 data/harness 挂到 window 上,保持原 .jsx 内部零修改
window.DATA_META = DATA_META;
window.POSTS = POSTS;
window.BOOK_COVERS = BOOK_COVERS;
window.getBookCover = getBookCover;
window.ContentService = ContentService;
window.CoverDownload = CoverDownload;
window.useContentStore = useContentStore;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

// 注册 PWA Service Worker (Network First HTML + Stale-While-Revalidate 静态资源 + Cache First 封面)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[sw] 注册失败:', err);
    });
  });
}

// === 新版本自动 reload ===
// 部署时 sw.js 会被替换(ETag 变),20s 轮询一次,检测到新版本就 toast + 自动 reload
// tab 可见时跑,隐藏时停(省电 / 避免对休眠页刷请求)
const VERSION_CHECK_INTERVAL = 20_000; // 20s(部署后 20s 内多数用户能感知)
const VERSION_CHECK_URL = '/sw.js?t=';  // query string 绕 SW 缓存,直接打网络
let currentVersion = null;
let versionCheckTimer = null;

function showUpdateToast() {
  let toast = document.getElementById('xhs-update-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'xhs-update-toast';
    toast.innerHTML = `
      <div style="position:fixed;bottom:24px;left:50%;transform:translateX(-50%);
                  background:#111;color:#fff;padding:14px 22px;border-radius:999px;
                  box-shadow:0 8px 24px rgba(0,0,0,0.25);z-index:9999;
                  font-family:system-ui,sans-serif;font-size:14px;display:flex;align-items:center;gap:12px;">
        <span>新数据已就绪</span>
        <button id="xhs-update-reload" style="background:#e11d48;color:#fff;border:none;
                  padding:6px 14px;border-radius:999px;cursor:pointer;font-size:13px;font-weight:600;">
          立即刷新
        </button>
      </div>
    `;
    document.body.appendChild(toast);
    document.getElementById('xhs-update-reload').onclick = () => {
      window.location.reload();
    };
  }
}

async function checkVersion() {
  try {
    const res = await fetch(VERSION_CHECK_URL + Date.now(), { cache: 'no-store' });
    if (!res.ok) return;
    let versionTag = res.headers.get('etag') || res.headers.get('last-modified');
    if (!versionTag) {
      const text = await res.text();
      versionTag = text.slice(0, 200);
    }
    if (currentVersion === null) {
      currentVersion = versionTag;
      console.log('[auto-update] first check, versionTag =', versionTag);
      return;
    }
    if (versionTag !== currentVersion) {
      console.log('[auto-update] NEW VERSION detected:', versionTag);
      showUpdateToast();
      // 3s 后自动 reload
      setTimeout(() => window.location.reload(), 3000);
    }
  } catch (e) {
    // 离线或网络问题,静默
  }
}

function startVersionCheck() {
  if (versionCheckTimer) return;
  checkVersion();
  versionCheckTimer = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
  console.log('[auto-update] started, poll every 20s while tab visible');
}

function stopVersionCheck() {
  if (versionCheckTimer) {
    clearInterval(versionCheckTimer);
    versionCheckTimer = null;
    console.log('[auto-update] paused (tab hidden)');
  }
}

if (import.meta.env.PROD) {
  // 5s 后首次(等 SW 注册完 + 首屏),tab 可见时跑
  setTimeout(() => {
    if (document.visibilityState === 'visible') {
      startVersionCheck();
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        startVersionCheck();
      } else {
        stopVersionCheck();
      }
    });
  }, 5000);
}
